import React, { useMemo, useState } from "react";
import type {
  TeacherLesson,
  TeacherOverviewActivity,
} from "../../api/dashboard";

// ─── DayRail ────────────────────────────────────────────────────────────────
// One horizontal track for a teaching day: every period drawn to scale, the
// gaps between them left empty so free time reads as free time, and a live
// marker for the current moment.
//
// This replaces the block of dead space the hero card used to be on a day
// with nothing left to teach. A list of three lessons tells a teacher what
// they teach; the rail tells them what their *day* looks like — where the
// double period is, where the two-hour gap is, how much is left.
// ─────────────────────────────────────────────────────────────────────────────

const FALLBACK = "#3B82F6";
const ACTIVITY_COLOR = "#10B981";

/**
 * Readable ink for a block painted in an arbitrary subject colour.
 *
 * Subject colours are generated by the school's own golden-angle allocator, so
 * they land anywhere on the wheel — pale yellows and dark violets alike. White
 * label text was hard-coded here and disappeared on every light subject. The
 * ink is picked from the fill's own relative luminance instead (WCAG 2.x
 * definition), which is exactly the comparison the contrast ratio is built on.
 *
 * The rail itself no longer needs this: it tints with the subject colour rather
 * than filling with it, so labels use the app's ink tokens. Kept for anywhere a
 * solid fill of an arbitrary hue is genuinely wanted — call it rather than
 * guessing black or white.
 */
export const readableInk = (hex: string): { ink: string; sub: string } => {
  const normalised = hex.replace("#", "").trim();
  const full =
    normalised.length === 3
      ? normalised
          .split("")
          .map((c) => c + c)
          .join("")
      : normalised;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    return { ink: "#ffffff", sub: "rgba(255,255,255,0.78)" };
  }

  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const r = channel(parseInt(full.slice(0, 2), 16));
  const g = channel(parseInt(full.slice(2, 4), 16));
  const b = channel(parseInt(full.slice(4, 6), 16));
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Contrast against white is (1.05)/(L+0.05); against near-black it is
  // (L+0.05)/0.05. They cross at L ≈ 0.179 — above it, dark ink wins.
  return luminance > 0.179
    ? { ink: "#0b1220", sub: "rgba(11,18,32,0.72)" }
    : { ink: "#ffffff", sub: "rgba(255,255,255,0.78)" };
};

const toMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + (m || 0);
};

/** Minutes since midnight -> "HH:MM". */
const clock = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/** Whole-hour tick label. */
const labelFor = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:00`;

export interface DayRailEntry {
  id: string;
  name: string;
  sub: string | null;
  start: number;
  end: number;
  color: string;
  kind: "lesson" | "activity";
}

export const railEntries = (
  lessons: TeacherLesson[],
  activities: TeacherOverviewActivity[],
): DayRailEntry[] =>
  [
    ...lessons.map((l) => ({
      id: `lesson-${l.slot_id}`,
      name: l.subject_name || "Lesson",
      sub: [l.class_group_name, l.location].filter(Boolean).join(" · ") || null,
      start: toMinutes(l.start_time),
      end: toMinutes(l.end_time),
      color: l.color || FALLBACK,
      kind: "lesson" as const,
    })),
    ...activities.map((a) => ({
      id: `activity-${a.activity_id}`,
      name: a.activity_name,
      sub: [a.activity_type, a.location].filter(Boolean).join(" · ") || null,
      start: toMinutes(a.start_time),
      end: toMinutes(a.end_time),
      color: a.color || ACTIVITY_COLOR,
      kind: "activity" as const,
    })),
  ].sort((a, b) => a.start - b.start);

const DayRail: React.FC<{
  entries: DayRailEntry[];
  /** Minutes since midnight, or null when the rail isn't showing today. */
  nowMinutes: number | null;
}> = ({ entries, nowMinutes }) => {
  const [hovered, setHovered] = useState<string | null>(null);

  const { from, to, ticks } = useMemo(() => {
    if (entries.length === 0) return { from: 0, to: 0, ticks: [] as number[] };
    // Snap to whole hours either side so the tick labels line up with the
    // track edges, and leave a little room for the "now" marker to sit in.
    const first =
      Math.floor(Math.min(...entries.map((e) => e.start)) / 60) * 60;
    const last = Math.ceil(Math.max(...entries.map((e) => e.end)) / 60) * 60;
    const start = first;
    const end = Math.max(last, start + 60);
    const hours: number[] = [];
    for (let m = start; m <= end; m += 60) hours.push(m);
    return { from: start, to: end, ticks: hours };
  }, [entries]);

  if (entries.length === 0) return null;

  const span = to - from;
  const pct = (minutes: number) => ((minutes - from) / span) * 100;
  const nowInRange =
    nowMinutes !== null && nowMinutes >= from && nowMinutes <= to;

  return (
    <div className="select-none">
      <div className="relative h-16 rounded-2xl bg-slate-100 dark:bg-slate-800/50 ring-1 ring-inset ring-border-light dark:ring-border-dark/40 overflow-hidden">
        {/* Hour gridlines — quiet structure behind the blocks. */}
        {ticks.slice(1, -1).map((m) => (
          <div
            key={`grid-${m}`}
            className="absolute top-0 bottom-0 w-px bg-border-light/70 dark:bg-border-dark/30"
            style={{ left: `${pct(m)}%` }}
            aria-hidden
          />
        ))}

        {entries.map((entry) => {
          const widthPct = Math.max(pct(entry.end) - pct(entry.start), 2.5);
          // A label clipped to "Deve…" is worse than no label: below roughly
          // a tenth of the track there is no room for one, and the hover
          // caption and tooltip carry the name instead.
          const fitsLabel = widthPct >= 10;
          const fitsSubLabel = widthPct >= 18;
          const isPast = nowMinutes !== null && entry.end <= nowMinutes;
          const isLive =
            nowMinutes !== null &&
            entry.start <= nowMinutes &&
            entry.end > nowMinutes;
          return (
            <button
              key={entry.id}
              type="button"
              onMouseEnter={() => setHovered(entry.id)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(entry.id)}
              onBlur={() => setHovered(null)}
              className={`absolute top-2 bottom-2 rounded-xl pl-2.5 pr-2 text-left overflow-hidden border transition-[transform,opacity,box-shadow] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                isPast ? "opacity-50" : "opacity-100"
              } ${isLive ? "ring-2 ring-offset-1 ring-blue-500 ring-offset-slate-100 dark:ring-offset-slate-900 shadow-lg" : ""} hover:-translate-y-0.5`}
              style={{
                left: `${pct(entry.start)}%`,
                width: `${widthPct}%`,
                // The subject colour identifies the lesson; it does not fill it. A saturated
                // slab meant every subject painted a different mood over the page (and a
                // lime or pale hue fought both themes), so the hue is now a tint plus a
                // spine and the text uses the app's ink — legible whatever colour is chosen.
                background: `color-mix(in oklab, ${entry.color} 20%, transparent)`,
                borderColor: `color-mix(in oklab, ${entry.color} 38%, transparent)`,
                boxShadow: `inset 3px 0 0 0 ${entry.color}`,
              }}
              title={`${entry.name} · ${clock(entry.start)}–${clock(entry.end)}`}
              aria-label={`${entry.name}, ${clock(entry.start)} to ${clock(entry.end)}`}
            >
              {fitsLabel && (
                <span className="block text-[11px] font-semibold leading-tight truncate text-slate-900 dark:text-white">
                  {entry.name}
                </span>
              )}
              {fitsSubLabel && entry.sub && (
                <span className="block text-[10px] leading-tight truncate text-slate-600 dark:text-slate-300">
                  {entry.sub}
                </span>
              )}
            </button>
          );
        })}

        {/* Live "now" marker. */}
        {nowInRange && (
          <div
            className="absolute top-0 bottom-0 z-10 w-0.5 bg-red-500 pointer-events-none"
            style={{ left: `${pct(nowMinutes!)}%` }}
            aria-hidden
          >
            <span className="absolute -top-0.5 -left-[3px] block w-2 h-2 rounded-full bg-red-500 ring-2 ring-red-500/30 animate-pulse" />
          </div>
        )}
      </div>

      <div className="relative mt-1 h-4">
        {ticks.map((m) => (
          <span
            key={`tick-${m}`}
            className="absolute -translate-x-1/2 text-[10px] tabular-nums text-text-secondary-light dark:text-text-secondary-dark"
            style={{ left: `${pct(m)}%` }}
          >
            {labelFor(m)}
          </span>
        ))}
      </div>

      {/* Hovering a block names it in full, so a narrow period is still
          readable without a tooltip library. */}
      <p className="mt-1 h-4 text-xs text-text-secondary-light dark:text-text-secondary-dark truncate">
        {(() => {
          const entry = entries.find((e) => e.id === hovered);
          if (!entry) return "";
          return `${clock(entry.start)}–${clock(entry.end)} · ${entry.name}${entry.sub ? ` · ${entry.sub}` : ""}`;
        })()}
      </p>
    </div>
  );
};

export default DayRail;
